import { NgClass } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, output, signal, viewChildren } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CallbackWrapperService, PinRejectedSubject } from 'projects/login/src/app/services/callback-wrapper.service';

@Component({
  selector: 'pay-lib-pin-entry',
  imports: [ReactiveFormsModule, NgClass],
  templateUrl: './pin-entry.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PinEntryComponent {
  private readonly cbs = inject(CallbackWrapperService);

  // Inputs/Outputs using modern signal-based syntax
  readonly pinCount = input.required<number>();
  readonly valueEntered = output<string>();
  readonly pressedEnter = output();

  // ViewChildren as signal
  readonly pinInputRefs = viewChildren<ElementRef<HTMLInputElement>>('pinInput');

  // State signals
  readonly pinValues = signal<string[]>([]);
  readonly disabled = signal(false);

  constructor() {
    // Initialize pin values when pinCount changes
    effect(() => {
      const count = this.pinCount();
      this.pinValues.set(Array(count).fill(''));
    });

    // Focus first input after render
    afterNextRender(() => {
      this.focusInput(0);
    });

    // Handle pin rejection
    this.cbs.getSubject(PinRejectedSubject).subscribe(() => {
      this.reset();
    });
  }

  private reset(): void {
    this.pinValues.set(Array(this.pinCount()).fill(''));
    this.focusInput(0);
  }

  public focusInput(index: number): void {
    const inputs = this.pinInputRefs();
    if (inputs[index]) {
      inputs[index].nativeElement.focus();
    }
  }

  private isComplete(): boolean {
    return this.pinValues().every((v) => v.length === 1);
  }

  private emitIfComplete(): void {
    if (this.isComplete()) {
      this.valueEntered.emit(this.pinValues().join(''));
    }
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(0, 1);

    // Update signal
    this.pinValues.update((values) => {
      const updated = [...values];
      updated[index] = value;
      return updated;
    });

    // Sync input value
    input.value = value;

    if (value) {
      const nextIndex = index + 1;
      if (nextIndex < this.pinCount()) {
        this.focusInput(nextIndex);
      } else {
        this.emitIfComplete();
      }
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      if (!input.value && index > 0) {
        this.focusInput(index - 1);
      }
      return;
    }

    if (event.key === 'Enter') {
      if (this.isComplete()) {
        event.preventDefault();
        this.pressedEnter.emit();
      }
      return;
    }

    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault();

      this.pinValues.update((values) => {
        const updated = [...values];
        updated[index] = event.key;
        return updated;
      });

      input.value = event.key;

      const nextIndex = index + 1;
      if (nextIndex < this.pinCount()) {
        this.focusInput(nextIndex);
      } else {
        this.emitIfComplete();
      }
    } else if (!['Control', 'v', 'V'].includes(event.key)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const data = event.clipboardData?.getData('text');
    this.fillFromString(data);
  }

  handleAutofill(event: Event): void {
    const data = (event as InputEvent).data;
    this.fillFromString(data);
  }

  private fillFromString(data: string | null | undefined): void {
    if (!data || !/^\d+$/.test(data)) return;

    const digits = [...data].slice(0, this.pinCount());

    this.pinValues.update((values) => {
      const updated = [...values];
      digits.forEach((digit, i) => {
        if (i < updated.length) {
          updated[i] = digit;
        }
      });
      return updated;
    });

    // Update DOM inputs
    const inputs = this.pinInputRefs();
    digits.forEach((digit, i) => {
      if (inputs[i]) {
        inputs[i].nativeElement.value = digit;
      }
    });

    this.emitIfComplete();
  }

  isInvalid(index: number): boolean {
    const value = this.pinValues()[index];
    return value !== '' && !/^[0-9]$/.test(value);
  }
}
